/**
 * MarginBreakdownDivider — the rule that splits the plot in two and names each side (the
 * delivery calendar marks where delivered work ends and the open book begins).
 *
 * Rendered through Recharts' <Customized>, not <ReferenceLine>, for a real reason: a
 * ReferenceLine on a category axis can only sit at a band's CENTRE, and this line marks a
 * BOUNDARY — the last delivered month and the first open one belong on opposite sides of
 * it. <Customized> receives the chart's own axis map, so the rule lands exactly between
 * the two bands instead of half a month out.
 */

import type { Tokens } from './useTokens'

export interface Divider {
  before: number // bars left of the line
  leftLabel: string
  rightLabel: string
}

interface BandScale {
  (value: string): number | undefined
  bandwidth: () => number
  domain: () => string[]
}

interface CustomizedProps {
  xAxisMap?: Record<string, { scale?: unknown }>
  offset?: { top?: number; height?: number }
}

/** Exactly between band k−1 and band k: the right edge of one, the left edge of the next,
 * halved. Derived from the scale itself rather than from the axis's padding internals. */
function boundaryX(scale: BandScale, before: number): number | null {
  const domain = scale.domain()
  const prev = scale(domain[before - 1])
  const next = scale(domain[before])
  if (prev == null || next == null) return null
  return (prev + scale.bandwidth() + next) / 2
}

export default function MarginBreakdownDivider({
  divider, barCount, t, ...chart
}: { divider: Divider; barCount: number; t: Tokens } & CustomizedProps) {
  const show = divider.before > 0 && divider.before < barCount
  const scale = chart.xAxisMap?.[Object.keys(chart.xAxisMap ?? {})[0]]?.scale as BandScale | undefined
  if (!show || !scale?.bandwidth) return null
  const x = boundaryX(scale, divider.before)
  if (x == null) return null
  const top = chart.offset?.top ?? 0
  const bottom = top + (chart.offset?.height ?? 0)
  return (
    <g>
      <line x1={x} x2={x} y1={top} y2={bottom} stroke={t.mut} strokeWidth={1} strokeDasharray="4 3" />
      <text x={x - 6} y={top + 10} fill={t.mut2} fontSize={9} textAnchor="end" fontFamily="JetBrains Mono">
        {divider.leftLabel}
      </text>
      <text x={x + 6} y={top + 10} fill={t.mut2} fontSize={9} textAnchor="start" fontFamily="JetBrains Mono">
        {divider.rightLabel}
      </text>
    </g>
  )
}
